import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, Leaf, Sparkles, Gem, Star, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import Reveal from "@/components/Reveal";
import SeoHead from "@/components/SeoHead";
import JsonLd, { buildWebPageSchema, buildBreadcrumbSchema } from "@/components/JsonLd";

const ICONS = { Heart, Leaf, Sparkles, Gem, Star };

const About = () => {
  const [data, setData] = useState(null);
  const { scrollYProgress } = useScroll();
  const parY = useTransform(scrollYProgress, [0, 1], [0, -180]);

  useEffect(() => {
    api.get("/pages/about").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-mauve">
        Loading…
      </div>
    );
  }

  const values = data.values || [];
  const stats = data.stats || [];
  const pageUrl = typeof window !== "undefined" ? window.location.origin + "/about" : "/about";

  return (
    <div className="font-body text-charcoal" data-testid="about-page">
      <SeoHead
        title={data.seo_title || data.title || "About — Rieave"}
        description={data.seo_description || data.hero_subtitle || "Discover the story behind Rieave — handcrafted jewelry that celebrates you."}
        keywords={data.seo_keywords}
        image={data.og_image || data.hero_image}
        canonical={pageUrl}
        robots={data.seo_robots}
      />
      <JsonLd
        data={[
          buildBreadcrumbSchema([
            { name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
            { name: "About", url: pageUrl },
          ]),
          buildWebPageSchema({
            name: data.seo_title || data.title || "About",
            description: data.seo_description || data.hero_subtitle,
            url: pageUrl,
            type: "AboutPage",
          }),
        ].filter(Boolean)}
      />
      {/* HERO — split type / image with parallax */}
      <section className="relative overflow-hidden" data-testid="about-hero">
        <div className="rieave-container pt-28 md:pt-36 pb-16 md:pb-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <motion.p
              className="eyebrow mb-5"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              data-testid="about-hero-eyebrow"
            >
              {data.hero_eyebrow || "Our story"}
            </motion.p>
            <motion.h1
              className="font-hero text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95]"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              {(data.hero_title || "").split(" ").slice(0, -1).join(" ")}{" "}
              <span className="serif-italic text-rose">{(data.hero_title || "").split(" ").slice(-1).join(" ")}</span>
            </motion.h1>
            <motion.p
              className="mt-7 max-w-md text-lg text-charcoal/70"
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }}
            >
              {data.hero_subtitle}
            </motion.p>
            <motion.div
              className="mt-10 flex items-center gap-5"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.4 }}
            >
              <Link to="/shop" className="btn-primary" data-testid="about-shop-cta">Shop the collection <ArrowRight size={14} className="ml-2" /></Link>
              <Link to="/contact" className="btn-link">Say hello</Link>
            </motion.div>
          </div>

          <div className="md:col-span-6 relative">
            <motion.div
              className="relative aspect-[4/5] overflow-hidden rounded-[2px]"
              initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2 }}
            >
              <motion.img
                src={data.hero_image}
                alt="rieave craftsmanship"
                className="w-full h-full object-cover"
                style={{ y: parY }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/20 via-transparent to-transparent" />
            </motion.div>
            {/* floating stat badge */}
            {stats[0] && (
              <motion.div
                className="absolute -bottom-6 -left-6 md:-left-10 bg-cream border border-charcoal/10 rounded-2xl px-6 py-4 shadow-xl"
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}
              >
                <div className="font-hero text-3xl text-charcoal">{stats[0].value}</div>
                <div className="eyebrow mt-1">{stats[0].label}</div>
              </motion.div>
            )}
          </div>
        </div>
        {/* decorative gradient blob */}
        <div className="absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle, #F3D6E2 0%, transparent 70%)" }} />
      </section>

      {/* STORY — editorial two-column with staggered images */}
      <section className="bg-cream" data-testid="about-story">
        <div className="rieave-container py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5 order-2 md:order-1 relative">
            <div className="relative">
              <Reveal y={30}>
                <img src={data.story_image_1} alt="" className="w-[82%] aspect-[4/5] object-cover rounded-[2px]" />
              </Reveal>
              <Reveal y={30} delay={0.15}>
                <img
                  src={data.story_image_2}
                  alt=""
                  className="absolute right-0 bottom-[-40px] w-[62%] aspect-[3/4] object-cover rounded-[2px] border-[8px] border-cream shadow-xl"
                />
              </Reveal>
            </div>
          </div>
          <div className="md:col-span-7 order-1 md:order-2 md:pl-10">
            <Reveal>
              <p className="eyebrow mb-4">The beginning</p>
              <h2 className="font-hero text-4xl md:text-5xl leading-[1.05] mb-6">
                {data.story_title}
              </h2>
              <p className="text-lg leading-[1.7] text-charcoal/75">
                {data.story_body}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS strip */}
      {stats.length > 0 && (
        <section className="relative py-14" style={{ background: "linear-gradient(135deg, #F3D6E2 0%, #E8B8CE 50%, #D4B896 100%)" }} data-testid="about-stats">
          <div className="rieave-container grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="text-center text-charcoal">
                  <div className="font-hero text-4xl md:text-6xl leading-none">{s.value}</div>
                  <div className="eyebrow mt-2 text-charcoal/60">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* VALUES — 4-up cards with icon reveal */}
      <section className="bg-[#FBF7F4]" data-testid="about-values">
        <div className="rieave-container py-24 md:py-32">
          <div className="max-w-2xl mb-14">
            <Reveal>
              <p className="eyebrow mb-3">What we stand for</p>
              <h2 className="font-hero text-4xl md:text-5xl">{data.mission_title || "Our promise"}</h2>
              {data.mission_body && <p className="mt-5 text-lg text-charcoal/70">{data.mission_body}</p>}
            </Reveal>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((v, i) => {
              const Icon = ICONS[v.icon] || Sparkles;
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ duration: 0.3 }}
                    className="group relative p-8 bg-cream rounded-[2px] border border-charcoal/8 h-full"
                  >
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-6 transition-colors group-hover:bg-rose/10" style={{ background: "#F3D6E2" }}>
                      <Icon size={20} className="text-rose" />
                    </div>
                    <h3 className="font-hero text-2xl mb-3">{v.title}</h3>
                    <p className="text-charcoal/65 text-[15px] leading-[1.6]">{v.body}</p>
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-rose scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOUNDER — pull-quote */}
      {data.founder_quote && (
        <section className="bg-charcoal text-cream relative overflow-hidden" data-testid="about-founder">
          <div className="rieave-container py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-5">
              <Reveal>
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={data.founder_image} alt={data.founder_name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-charcoal/40 to-transparent" />
                </div>
              </Reveal>
            </div>
            <div className="md:col-span-7">
              <Reveal>
                <p className="eyebrow mb-5 text-gold">A note from the founder</p>
                <p className="font-hero text-3xl md:text-5xl leading-[1.15] mb-8">
                  <span className="text-gold serif-italic">&ldquo;</span>{data.founder_quote}<span className="text-gold serif-italic">&rdquo;</span>
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-px bg-gold" />
                  <div>
                    <div className="font-cormorant text-xl">{data.founder_name}</div>
                    <div className="eyebrow text-cream/50">{data.founder_role}</div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full opacity-20 blur-3xl" style={{ background: "radial-gradient(circle, #C9789A 0%, transparent 70%)" }} />
        </section>
      )}

      {/* CTA */}
      <section className="bg-cream" data-testid="about-cta">
        <div className="rieave-container py-24 text-center">
          <Reveal>
            <p className="eyebrow mb-4">Continue the story</p>
            <h2 className="font-hero text-4xl md:text-6xl mb-8">
              Find your <span className="serif-italic text-rose">forever</span> piece.
            </h2>
            <div className="flex flex-wrap items-center justify-center gap-5">
              <Link to="/shop" className="btn-primary" data-testid="about-footer-cta-shop">Shop the edit</Link>
              <Link to="/contact" className="btn-outline">Write to us</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
